import { queryOptions } from '@tanstack/react-query';

import type {
  DealRecord,
  ListDealRecordsInput,
  PaymentAccountDetail,
  SettlementRequest,
} from '@marxbiotech/shared-types/bff';

import { api } from '@lib/api/client';

// Re-export shared types for convenience
export type {
  DealRecord,
  PaymentAccountDetail,
  SettlementRequest,
} from '@marxbiotech/shared-types/bff';

interface PaymentAccountDetailResponse {
  account: PaymentAccountDetail;
}

export interface CreditCapacity {
  creditLimit: number;
  usedCredit: number;
  availableCredit: number;
  expectedDepositAmount: number;
}

interface DealRecordsResponse {
  deal_records: DealRecord[];
}

// =============================================================================
// Types
// =============================================================================

interface SettlementRequestsResponse {
  settlement_requests: SettlementRequest[];
}

// =============================================================================
// Query Keys
// =============================================================================

export const paymentKeys = {
  all: ['payment'] as const,
  accountDetail: () =>
    [...paymentKeys.all, 'accountDetail'] as const,
  creditCapacity: () =>
    [...paymentKeys.all, 'creditCapacity'] as const,
  dealRecords: (params?: ListDealRecordsInput) =>
    [...paymentKeys.all, 'dealRecords', params] as const,
  dealRecordsByTime: (params?: ListDealRecordsInput) =>
    [...paymentKeys.all, 'dealRecordsByTime', params] as const,
  settlementRequests: (loanRequestId?: string) =>
    [...paymentKeys.all, 'settlementRequests', loanRequestId] as const,
  pendingSettlementRequests: () =>
    [...paymentKeys.all, 'pendingSettlementRequests'] as const,
  monthlySettlement: () =>
    [...paymentKeys.all, 'monthlySettlement'] as const,
};

// =============================================================================
// Query Options
// =============================================================================

export const dealRecordsQueryOptions = (
  params?: ListDealRecordsInput
) =>
  queryOptions({
    queryKey: paymentKeys.dealRecords(params),
    queryFn: async (): Promise<DealRecord[]> => {
      const queryParams = new URLSearchParams();

      if (params?.types) {
        const types = Array.isArray(params.types) ? params.types : [params.types];
        types.forEach((type) => queryParams.append('types', type));
      }
      if (params?.limit) queryParams.set('limit', String(params.limit));
      if (params?.offset) queryParams.set('offset', String(params.offset));
      if (params?.dealtAtGte) queryParams.set('dealtAtGte', params.dealtAtGte);
      if (params?.dealtAtLt) queryParams.set('dealtAtLt', params.dealtAtLt);
      if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params?.orderBy) queryParams.set('orderBy', params.orderBy);

      const url = `/payment/accounts/current/deal-records?${queryParams.toString()}`;
      const response = await api.get<DealRecordsResponse>(url);
      return response.data.deal_records;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/**
 * Query options for getting the latest deal record by time
 * Used for deposit/monthly settlement confirmation modals
 */
export const latestDealRecordQueryOptions = (
  dealtAtLt: string
) =>
  queryOptions({
    queryKey: paymentKeys.dealRecordsByTime({ dealtAtLt, limit: 1 }),
    queryFn: async (): Promise<DealRecord | null> => {
      const queryParams = new URLSearchParams();
      queryParams.set('limit', '1');
      queryParams.set('dealtAtLt', dealtAtLt);

      const url = `/payment/accounts/current/deal-records?${queryParams.toString()}`;
      const response = await api.get<DealRecordsResponse>(url);
      return response.data.deal_records[0] ?? null;
    },
    enabled: !!dealtAtLt,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/**
 * Query options for getting settlement requests by loan request ID
 * Used for order payment notification in order details
 */
export const settlementRequestsQueryOptions = (
  loanRequestId: string
) =>
  queryOptions({
    queryKey: paymentKeys.settlementRequests(loanRequestId),
    queryFn: async (): Promise<SettlementRequest[]> => {
      const queryParams = new URLSearchParams();
      queryParams.set('loanRequestId', loanRequestId);

      const url = `/payment/accounts/current/settlement-requests?${queryParams.toString()}`;
      const response = await api.get<SettlementRequestsResponse>(url);
      return response.data.settlement_requests;
    },
    enabled: !!loanRequestId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/**
 * Query options for getting payment account detail
 */
export const paymentAccountDetailQueryOptions = () =>
  queryOptions({
    queryKey: paymentKeys.accountDetail(),
    queryFn: async (): Promise<PaymentAccountDetail> => {
      const response = await api.get<PaymentAccountDetailResponse>(
        '/payment/accounts/current'
      );
      return response.data.account;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/**
 * Query options for getting credit capacity
 */
export const creditCapacityQueryOptions = () =>
  queryOptions({
    queryKey: paymentKeys.creditCapacity(),
    queryFn: async (): Promise<CreditCapacity> => {
      const response = await api.get<CreditCapacity>(
        '/payment/accounts/current/credit-capacity'
      );
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

/**
 * Query options for getting pending settlement requests
 */
export const pendingSettlementRequestsQueryOptions = () =>
  queryOptions({
    queryKey: paymentKeys.pendingSettlementRequests(),
    queryFn: async (): Promise<SettlementRequest[]> => {
      const response = await api.get<SettlementRequestsResponse>(
        '/payment/accounts/current/settlement-requests/pending'
      );
      return response.data.settlement_requests;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
